import { useState } from 'react'
import { getSnapshotFile } from '../api/vestige.js'

function formatSize(bytes) {
  if (bytes == null) return ''
  if (bytes < 1024) return `${bytes}b`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}k`
  return `${(bytes / (1024 * 1024)).toFixed(1)}M`
}

function buildTree(files) {
  const dirs = new Set()
  const byDir = {}
  for (const f of files) {
    if (f.isDir) continue
    const parts = f.path.split('/')
    const dir = parts.length > 1 ? parts.slice(0, -1).join('/') : ''
    if (!byDir[dir]) byDir[dir] = []
    byDir[dir].push({ ...f, name: parts[parts.length - 1] })
    // collect all ancestor dirs
    for (let i = 1; i < parts.length; i++) {
      dirs.add(parts.slice(0, i).join('/'))
    }
  }
  return { byDir, dirs: Array.from(dirs).sort() }
}

export default function FileExplorer({ files, workspaceId, snapshotId }) {
  const [openDirs, setOpenDirs] = useState(new Set(['']))
  const [selectedPath, setSelectedPath] = useState(null)
  const [content, setContent] = useState(null)
  const [loading, setLoading] = useState(false)

  if (!files || files.length === 0) {
    return (
      <div className="text-xs text-text-faint text-center py-6 font-mono">
        No files stored for this snapshot
      </div>
    )
  }

  const { byDir, dirs } = buildTree(files)

  const toggleDir = (dir) => {
    setOpenDirs((prev) => {
      const next = new Set(prev)
      if (next.has(dir)) next.delete(dir)
      else next.add(dir)
      return next
    })
  }

  const openFile = async (path) => {
    setSelectedPath(path)
    setContent(null)
    setLoading(true)
    try {
      const res = await getSnapshotFile(workspaceId, snapshotId, path)
      setContent(res.content ?? '')
    } catch {
      setContent('(failed to load)')
    } finally {
      setLoading(false)
    }
  }

  // Render root files + each directory
  const renderDir = (dirKey, depth = 0) => {
    const dirFiles = byDir[dirKey] ?? []
    const subDirs = dirs.filter((d) => {
      const parent = d.includes('/') ? d.slice(0, d.lastIndexOf('/')) : ''
      return parent === dirKey
    })
    return (
      <div key={dirKey}>
        {subDirs.map((subDir) => {
          const name = subDir.includes('/') ? subDir.slice(subDir.lastIndexOf('/') + 1) : subDir
          const isOpen = openDirs.has(subDir)
          return (
            <div key={subDir}>
              <button
                onClick={() => toggleDir(subDir)}
                className="w-full text-left px-2 py-0.5 flex items-center gap-1 hover:bg-surface text-text-dim"
                style={{ paddingLeft: `${8 + depth * 12}px` }}
              >
                <span className="text-text-faint text-2xs">{isOpen ? '▾' : '▸'}</span>
                <span className="text-2xs font-mono text-text-dim">{name}/</span>
              </button>
              {isOpen && renderDir(subDir, depth + 1)}
            </div>
          )
        })}
        {dirFiles.map((f) => (
          <button
            key={f.path}
            onClick={() => openFile(f.path)}
            className={`w-full text-left px-2 py-0.5 flex items-center justify-between gap-1 transition-colors
              ${selectedPath === f.path ? 'bg-accent/15 text-text' : 'hover:bg-surface text-text-dim'}`}
            style={{ paddingLeft: `${8 + (depth + 1) * 12}px` }}
          >
            <span className="text-2xs font-mono truncate">{f.name}</span>
            <span className="text-2xs text-text-faint flex-shrink-0">{formatSize(f.size)}</span>
          </button>
        ))}
      </div>
    )
  }

  return (
    <div className="flex gap-0 border border-border rounded overflow-hidden" style={{ minHeight: '180px', maxHeight: '420px' }}>
      {/* Tree */}
      <div className="w-48 flex-shrink-0 border-r border-border bg-surface2 overflow-y-auto">
        {renderDir('')}
      </div>

      {/* Content panel */}
      <div className="flex-1 overflow-auto bg-ground">
        {loading && (
          <div className="text-xs text-text-faint text-center py-8 font-mono">Loading…</div>
        )}
        {!loading && content === null && (
          <div className="text-xs text-text-faint text-center py-8 font-mono">
            Select a file to view
          </div>
        )}
        {!loading && content !== null && (
          <div>
            <div className="px-3 py-1.5 text-2xs font-mono text-text-dim border-b border-border bg-surface2">
              {selectedPath}
            </div>
            <pre className="px-3 py-3 text-2xs font-mono text-text-dim leading-5 whitespace-pre-wrap break-words overflow-x-hidden">
              {content}
            </pre>
          </div>
        )}
      </div>
    </div>
  )
}
